import React, { useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, ShieldCheck, Check, ArrowRight, ExternalLink, Lock, User, AlertTriangle } from "lucide-react";

export default function Documents() {
  const { user, refresh } = useAuth();
  const verificationStatus = user?.verification_status || (user?.documents_verified ? "verified" : "pending");
  const locked = !!user?.documents_verified;
  const docsToReupload = user?.documents_to_reupload || [];

  // Documents are only locked once verified — freely editable at every
  // stage before that (pending review or rejected).
  const isFieldLocked = (field) => !!user?.documents_verified;

  const [photo,    setPhoto]    = useState(user?.profile_photo || "");
  const [passport, setPassport] = useState(user?.passport_document || "");
  const [medical,  setMedical]  = useState(user?.medical_certificate || "");
  const [saving,   setSaving]   = useState(false);

  const [pendingPhoto,    setPendingPhoto]    = useState(null);
  const [pendingPassport, setPendingPassport] = useState(null);
  const [pendingMedical,  setPendingMedical]  = useState(null);
  const [photoPreview,    setPhotoPreview]    = useState(user?.profile_photo || "");

  const uploadToS3 = async (file, type = "document") => {
    const fd = new FormData();
    fd.append("file", file);
    const endpoint = type === "photo" ? "/api/upload/photo" : "/api/upload/document";
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Upload failed");
    }
    return (await res.json()).url;
  };

  const onDoc = (setUrl, setPending, field) => (e) => {
    if (isFieldLocked(field)) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setPending(file);
    setUrl(file.name);
  };

  const onPhoto = (e) => {
    if (isFieldLocked("profile_photo")) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Photo must be under 3MB"); return; }
    setPendingPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setPhoto(file.name);
  };

  const canSave = (pendingPhoto || pendingPassport || pendingMedical) && !locked;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      toast.loading("Uploading files…", { id: "doc-upload" });
      const [photoUrl, passportUrl, medicalUrl] = await Promise.all([
        pendingPhoto    ? uploadToS3(pendingPhoto, "photo") : Promise.resolve(user?.profile_photo || ""),
        pendingPassport ? uploadToS3(pendingPassport)       : Promise.resolve(user?.passport_document || ""),
        pendingMedical  ? uploadToS3(pendingMedical)        : Promise.resolve(user?.medical_certificate || ""),
      ]);
      toast.dismiss("doc-upload");
      await api.put("/profile", {
        ...(pendingPhoto    ? { profile_photo:       photoUrl }    : {}),
        ...(pendingPassport ? { passport_document:   passportUrl } : {}),
        ...(pendingMedical  ? { medical_certificate: medicalUrl }  : {}),
      });
      if (pendingPhoto)    setPhoto(photoUrl);
      if (pendingPassport) setPassport(passportUrl);
      if (pendingMedical)  setMedical(medicalUrl);
      setPendingPhoto(null);
      setPendingPassport(null);
      setPendingMedical(null);
      await refresh?.();
      toast.success("Documents saved — pending re-verification");
    } catch (e) {
      toast.dismiss("doc-upload");
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const viewDoc = async (url) => {
    if (!url || !url.startsWith("http")) return;
    // Documents live in a private S3 bucket — the stored URL isn't directly
    // accessible, so fetch a short-lived signed URL to actually view it.
    // The tab must be opened synchronously (in the same tick as the click)
    // or browsers treat the later window.open as a non-user-initiated popup
    // and silently block it — so open a blank tab first, then navigate it.
    const key = url.includes(".amazonaws.com/") ? url.split(".amazonaws.com/")[1].split("?")[0] : "";
    if (!key) return;
    // Note: no "noopener" here — we need the window reference to navigate it
    // once the signed URL arrives. Safe since we only ever navigate it to
    // our own trusted S3 URL, never to arbitrary/user-controlled content.
    const tab = window.open("", "_blank");
    try {
      const { data } = await api.get("/upload/signed-url", { params: { key } });
      if (tab) tab.location.href = data.url;
    } catch (e) {
      if (tab) tab.close();
      toast.error(formatError(e.response?.data?.detail) || "Failed to open document");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-3xl mx-auto px-6 lg:px-10">
        <BackButton className="mb-6" />
        <div className="eyebrow mb-3">Required Documents</div>
        <h1 className="font-display font-black text-4xl md:text-5xl mb-2">
          Your <span className="text-[#00F0FF]">documents</span>
        </h1>
        <p className="text-[#94A3B8] mb-8">
          Per AMSC India 2026 rules, all athletes must submit {user?.nationality === "India" ? "an SFI UID document" : "a passport copy"} and a medical fitness certificate before final entry. PDF or image · max 5MB each.
        </p>

        {verificationStatus === "verified" && (
          <div className="flex items-center gap-3 mb-6 px-5 py-4 rounded-xl bg-[#00E676]/10 border border-[#00E676]/30">
            <Lock className="w-5 h-5 text-[#00E676] shrink-0" />
            <div>
              <div className="font-semibold text-[#00E676] text-sm">Documents Verified</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">Your documents have been approved by the organiser and can no longer be changed.</div>
            </div>
          </div>
        )}

        {verificationStatus === "pending" && (
          <div className="flex items-center gap-3 mb-6 px-5 py-4 rounded-xl bg-[#FBBF24]/10 border border-[#FBBF24]/30">
            <Lock className="w-5 h-5 text-[#FBBF24] shrink-0" />
            <div>
              <div className="font-semibold text-[#FBBF24] text-sm">Documents Under Review</div>
              <div className="text-xs text-[#94A3B8] mt-0.5">Your documents are currently being reviewed and cannot be changed until the review is complete.</div>
            </div>
          </div>
        )}

        {verificationStatus === "rejected" && (
          <div className="flex items-start gap-3 mb-6 px-5 py-4 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30">
            <AlertTriangle className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-[#EF4444] text-sm mb-1">Verification Not Approved</div>
              <div className="text-sm text-[#FCA5A5]">{user?.verification_reason || "Your documents were not approved by the organiser."}</div>
              <div className="text-xs text-[#94A3B8] mt-2">Re-upload the highlighted document(s) below and save.</div>
              <a href="/medical-certificate-draft.pdf" target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#00F0FF] hover:text-white transition-colors">
                <FileText className="w-3.5 h-3.5" /> Download Sample Medical Certificate
              </a>
            </div>
          </div>
        )}

        {/* Profile photo */}
        <div className="card-dark p-6 md:p-8 mb-4">
          <h2 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider mb-4">Profile Photo</h2>
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[#142040] border-2 border-[#2A3A5F] shrink-0 flex items-center justify-center">
              {(photoPreview && (photoPreview.startsWith("http") || photoPreview.startsWith("data:")))
                ? <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
                : <User className="w-8 h-8 text-[#475569]" />
              }
            </div>
            <div className="flex-1">
              <div className="font-semibold text-white mb-1 flex items-center gap-2 flex-wrap">
                Profile Photo
                {verificationStatus === "rejected" && docsToReupload.includes("profile_photo") && (
                  <span className="text-[11px] font-bold text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 px-2 py-0.5 rounded-full">Re-upload required</span>
                )}
              </div>
              <div className="text-xs text-[#94A3B8] mb-3">
                {isFieldLocked("profile_photo") ? "Verified & locked" : "JPEG or PNG · max 3MB"}
              </div>
              {!isFieldLocked("profile_photo") ? (
                <label className="text-xs uppercase tracking-wider border border-[#00F0FF] text-[#00F0FF] px-3 py-1.5 rounded cursor-pointer hover:bg-[#00F0FF]/10 inline-block">
                  {photo ? "Replace" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
                </label>
              ) : (
                <div className="flex items-center gap-1 text-xs text-[#64748B]">
                  <Lock className="w-3 h-3" /> Locked
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Doc slots */}
        <div className="card-dark p-6 md:p-8 space-y-5">
          <h2 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider">Verification Documents</h2>
          <DocSlot
            label={user?.nationality === "India" ? "SFI UID Document" : "Passport Copy"}
            icon={FileText}
            value={passport}
            onChange={onDoc(setPassport, setPendingPassport, "passport_document")}
            onView={() => viewDoc(passport)}
            locked={isFieldLocked("passport_document")}
            reuploadRequired={verificationStatus === "rejected" && docsToReupload.includes("passport_document")}
            testid="passport-doc"
          />
          <DocSlot
            label="Medical Fitness Certificate"
            icon={ShieldCheck}
            value={medical}
            onChange={onDoc(setMedical, setPendingMedical, "medical_certificate")}
            onView={() => viewDoc(medical)}
            locked={isFieldLocked("medical_certificate")}
            reuploadRequired={verificationStatus === "rejected" && docsToReupload.includes("medical_certificate")}
            testid="medical-doc"
          />
          {!locked && (
            <button
              onClick={save}
              disabled={saving || !canSave}
              className="btn-primary w-full justify-center disabled:opacity-40"
              data-testid="save-docs-btn"
            >
              {saving ? "Saving…" : "Save Documents"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const DocSlot = ({ label, icon: Icon, value, onChange, onView, locked, reuploadRequired, testid }) => {
  const attached = !!value;
  return (
    <div
      className={`rounded-lg border p-5 ${reuploadRequired ? "border-[#EF4444]/50 bg-[#EF4444]/5" : attached ? "border-[#00E676]/40 bg-[#00E676]/5" : "border-[#2A3A5F] bg-[#0B1429]"}`}
      data-testid={testid}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`w-12 h-12 rounded flex items-center justify-center shrink-0 ${reuploadRequired ? "bg-[#EF4444]/15 text-[#EF4444]" : attached ? "bg-[#00E676]/15 text-[#00E676]" : "bg-[#142040] text-[#00F0FF]"}`}>
            {attached ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold flex items-center gap-2 flex-wrap">
              {label}
              {reuploadRequired && (
                <span className="text-[11px] font-bold text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 px-2 py-0.5 rounded-full">Re-upload required</span>
              )}
            </div>
            <div className="text-xs text-[#94A3B8]">
              {attached ? (locked ? (reuploadRequired ? "Needs replacement" : "Locked") : "Uploaded") : "Not yet uploaded"}
            </div>
          </div>
        </div>
        <div className="flex gap-3 items-center shrink-0 pl-16 sm:pl-0">
          {attached && value.startsWith("http") && (
            <button onClick={onView} className="text-xs text-[#00F0FF] hover:underline flex items-center gap-1" data-testid={`${testid}-view`}>
              <ExternalLink className="w-3 h-3" /> View
            </button>
          )}
          {!locked ? (
            <label className="text-xs uppercase tracking-wider border border-[#00F0FF] text-[#00F0FF] px-3 py-1.5 rounded cursor-pointer hover:bg-[#00F0FF]/10 shrink-0">
              {attached ? "Replace" : "Upload"}
              <input type="file" accept="application/pdf,image/*" className="hidden" onChange={onChange} />
            </label>
          ) : (
            <div className="flex items-center gap-1 text-xs text-[#64748B] px-3 py-1.5 shrink-0">
              <Lock className="w-3 h-3" /> Locked
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
